import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Radio, Button, Modal } from 'antd';
import { SubsContext } from './SubscriptionInfo';
import { getCardTypeName } from './plans';

const PaymentForm = styled.form`
  display: grid;
  gap: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const CardField = styled.div`
  width: 100%;
  height: 35px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  padding: 8px;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  margin-top: 8px;
  display: ${props => props.visible ? 'block' : 'none'};
`;

const TestCardInfo = styled.div`
  display: flex;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 20px;
  font-size: 14px;
`;

const TestCardColumn = styled.div`
  flex: 50%;
  padding: 10px;
`;

const TestCardTitle = styled.div`
  font-weight: bold;
  margin-bottom: 8px;
  color: #666;
`;

const TestCardNumber = styled.div`
  font-family: monospace;
  font-size: 16px;
  color: #007bff;
  margin-bottom: 4px;
`;

const HelperText = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
`;

const PaymentModal = ({
  visible,
  isLoading,
  error,
  onSubmit,
  onClose,
  customerInfo,
}) => {
  const {
    billsbyCustomerId,
    subscription,
    billsbyCard,
  } = useContext(SubsContext);
  const [newCreditCard, setNewCreditCard] = useState(!billsbyCustomerId || subscription && subscription.status === 'Suspended');
  const [errors, setErrors] = useState('');
  const [tokenizerReady, setTokenizerReady] = useState(false);
  const [isTokenizerLoading, setIsTokenizerLoading] = useState(false);

  useEffect(() => {
    setNewCreditCard(!billsbyCustomerId || subscription && subscription.status === 'Suspended')
  }, [billsbyCustomerId, subscription]);

  useEffect(() => {
    const initBillsby = async () => {
      try {
        // Set loading state for tokenizer
        setIsTokenizerLoading(true);
        setTokenizerReady(false);

        // Wait for billsbyTokens to be available
        if (!window.billsbyTokens) {
          await window.billsbyReady;
        }

        // Add debug log to check modal and card state
        console.log('PaymentModal debug - newCreditCard:', newCreditCard, 'billsbyCard:', billsbyCard);

        // Wait for DOM elements to be ready
        const waitForElements = () => new Promise((resolve) => {
          const checkElements = () => {
            const numberElement = document.getElementById('billsby-number');
            const cvvElement = document.getElementById('billsby-cvv');

            if (numberElement && cvvElement) {
              resolve();
            } else {
              setTimeout(checkElements, 100);
            }
          };
          checkElements();
        });

        await waitForElements();

        window.billsbyTokens.removeHandlers();
        window.billsbyTokens.init('billsby-number', 'billsby-cvv');

        // Add debug logs for tokenization
        window.billsbyTokens.on('ready', () => {
          console.log('Billsby tokenizer is ready');
          setTokenizerReady(true);
          setIsTokenizerLoading(false);
        });

        window.billsbyTokens.on('paymentMethod', (token, pmData) => {
          console.log('Token created:', token);
          console.log('PmData received:', pmData);
          setErrors('');
          onSubmit(pmData);
          setNewCreditCard(false);
        });

        window.billsbyTokens.on('errors', (tokenizationErrors) => {
          console.error('Billsby tokenization errors:', tokenizationErrors);
          if (tokenizationErrors && tokenizationErrors.length > 0) {
            const errorMessages = tokenizationErrors.map((err) => err.message);
            setErrors(errorMessages.join(', '));
          } else {
            setErrors('Unknown error occurred during payment processing');
          }
        });
      } catch (initError) {
        console.error('Failed to initialize Billsby:', initError);
        setErrors('Failed to initialize payment system. Please try again.');
        setIsTokenizerLoading(false);
      }
    };

    if (visible && newCreditCard) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        initBillsby();
      }, 100);
    }

    return () => {
      if (window.billsbyTokens) {
        window.billsbyTokens.removeHandlers();
      }
    };
  }, [visible, onSubmit, newCreditCard]);

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    // Check if we need to tokenize a new credit card
    if (newCreditCard) {
      console.log('Using tokenization for new credit card');

      // Ensure we have the month and year values
      const monthElement = document.getElementById('month');
      const yearElement = document.getElementById('year');

      const month = monthElement ? monthElement.value : null;
      const year = yearElement ? yearElement.value : null;

      if (!month || !year) {
        setErrors('Please select expiration month and year');
        return;
      }

      let fullName = 'Card Holder';
      if (customerInfo && customerInfo.firstName && customerInfo.lastName) {
        fullName = `${customerInfo.firstName} ${customerInfo.lastName}`;
      }

      // Use the tokenizer for new cards
      window.billsbyTokens.tokenizeCreditCard({
        full_name: fullName,
        month,
        year,
      });

      return;
    }

    // This path is only for existing cards (resubscribe with saved card)
    console.log('Using direct submission with existing card');
    onSubmit(null);
    // Modal will be closed by parent component after successful submission
  };

  return (
    <Modal
      width={700}
      title="Payment Information"
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={handleSubmit}
          loading={isLoading}
        >
          {billsbyCustomerId ? 'Confirm Change' : 'Confirm Subscription'}
        </Button>,
      ]}
    >
      <PaymentForm>
        {/* Loading overlay for tokenizer initialization */}
        {isTokenizerLoading && !tokenizerReady && newCreditCard && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '10px'
            }} />
            <div>Initializing payment system...</div>
            <div style={{ fontSize: '12px', marginTop: '5px' }}>Please wait a moment</div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}
        <h3>Payment Information</h3>
        <div style={{ display: (billsbyCustomerId && (!subscription || subscription.status !== 'Suspended') ? 'block' : 'none') }}>
          <Radio.Group
            onChange={e => setNewCreditCard(e.target.value)}
            value={!!newCreditCard}
          >
            <Radio value={false}>
              Use Previous Credit Card
              {billsbyCard && (
                <span style={{ marginLeft: '10px', color: '#666' }}>
                  ({getCardTypeName(billsbyCard.cardType)} ending in: {billsbyCard.cardLast4Digits} - Expires {billsbyCard.cardExpiryMonth}/{billsbyCard.cardExpiryYear})
                </span>
              )}
            </Radio>
            <br />
            <Radio value>Use New Credit Card</Radio>
          </Radio.Group>
        </div>
        <div hidden={!newCreditCard}>
          <TestCardInfo>
            <TestCardColumn>
              <TestCardTitle>Valid Test Cards</TestCardTitle>
              <TestCardNumber>4111 1111 1111 1111</TestCardNumber>
              <TestCardNumber>3782 8224 6310 005 </TestCardNumber>
              <TestCardNumber>5555 5555 5555 4444</TestCardNumber>
            </TestCardColumn>
            <TestCardColumn>
              <TestCardTitle>Declined Test Cards</TestCardTitle>
              <TestCardNumber>4012 8888 8888 1881</TestCardNumber>
              <TestCardNumber>3714 4963 5398 431 </TestCardNumber>
              <TestCardNumber>5105 1051 0510 5100</TestCardNumber>
            </TestCardColumn>
          </TestCardInfo>

          <FormGroup>
            <Label>Credit Card Number</Label>
            <CardField id="billsby-number" />
          </FormGroup>

          <FormGroup>
            <Label>Expiration Date</Label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <Input
                type="text"
                id="month"
                placeholder="MM"
                maxLength="2"
                style={{ width: '60px' }}
                pattern="[0-9]*"
                inputMode="numeric"
                required
                defaultValue={new Date().getMonth() + 1 < 10 ? `0${new Date().getMonth() + 1}` : new Date().getMonth() + 1}
              />
              <Input
                type="text"
                id="year"
                placeholder="YYYY"
                maxLength="4"
                style={{ width: '80px' }}
                pattern="[0-9]*"
                inputMode="numeric"
                required
                defaultValue={new Date().getFullYear() + 1}
              />
            </div>
          </FormGroup>

          <FormGroup>
            <Label>CVV</Label>
            <CardField id="billsby-cvv" style={{ width: '60px' }} />
            <HelperText>Enter any 3 digits (e.g., 123)</HelperText>
          </FormGroup>

          <ErrorMessage visible={!!error}>{error}</ErrorMessage>
          <ErrorMessage visible={!!errors}>{errors}</ErrorMessage>
        </div>
      </PaymentForm>
    </Modal >
  );
};

PaymentModal.propTypes = {
  visible: PropTypes.bool,
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  customerInfo: PropTypes.shape({
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
  }).isRequired,
};

PaymentModal.defaultProps = {
  visible: false,
  error: PropTypes.string.isRequired,
};

export default PaymentModal;



// WEBPACK FOOTER //
// ./src/components/Billsby/PaymentModal.jsx